import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../../hooks/useIsMobile';
import { Layout, Spin, message, Tag, Button } from 'antd';
import { TrophyOutlined, CalendarOutlined, TeamOutlined, ReloadOutlined } from '@ant-design/icons';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';
import type { TournamentResponseDto, RankingEntryDto } from '../../api-schema/generated/types';
import {
  HomeContainer,
  PageHeader,
  PageTitle,
  PageSubtitle,
} from '../Home/styles';
import { Card as StyledCard } from '../PairTrading/styles';

import type { PortfolioIdResponseDto } from '../../api-schema/generated/types';
import { formatNumber } from '../../utils/numberFormat';

const coreService = apiService.getService(ServiceType.CORE);
const { Content } = Layout;

/** ▲n / ▼n / — since the previous trading day's snapshot; empty when there is no earlier rank. */
const RankMove: React.FC<{ entry: RankingEntryDto }> = ({ entry }) => {
  const prev = entry.previousRank;
  const delta = entry.rankDelta;
  if (prev == null || delta == null) return <span />;
  const title = `#${prev} on ${entry.rankReference ?? 'the reference day'}`;
  if (delta === 0) return <span title={title} style={{ color: '#848E9C' }}>—</span>;
  const up = delta > 0;
  return (
    <span title={title} style={{ color: up ? '#0ECB81' : '#F6465D', fontWeight: 600, fontSize: 12 }}>
      {up ? '▲' : '▼'}{Math.abs(delta)}
    </span>
  );
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const formatDate = (dateStr: string) => {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
};

/** Whole-row highlight for the top 3, in their medal colours. */
const PODIUM: Record<number, { tint: string; edge: string }> = {
  1: { tint: 'rgba(255, 215, 0, 0.09)', edge: '#FFD700' },
  2: { tint: 'rgba(192, 192, 192, 0.07)', edge: '#C0C0C0' },
  3: { tint: 'rgba(205, 127, 50, 0.08)', edge: '#CD7F32' },
};

interface TournamentWithRanking extends TournamentResponseDto {
  ranking: RankingEntryDto[];
}

const TournamentPage: React.FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState<TournamentWithRanking[]>([]);
  const [loading, setLoading] = useState(true);
  const [myPortfolioNames, setMyPortfolioNames] = useState<Set<string>>(new Set());

  const fetchMyTournaments = useCallback(async () => {
    try {
      setLoading(true);
      const [response, portfoliosRes] = await Promise.all([
        coreService.get<TournamentResponseDto[]>('/tournament/my'),
        coreService.get<PortfolioIdResponseDto[]>('/portfolios'),
      ]);
      const portfolios = Array.isArray(portfoliosRes.data) ? portfoliosRes.data : [];
      setMyPortfolioNames(new Set(portfolios.map((p) => p.name ?? p.id)));
      const list = Array.isArray(response.data) ? response.data : [];
      const tournamentsWithRanking: TournamentWithRanking[] = await Promise.all(
        list.map(async (t) => {
          try {
            const rankRes = await coreService.get<RankingEntryDto[]>(`/tournament/${t.id}/ranking`);
            return { ...t, ranking: Array.isArray(rankRes.data) ? rankRes.data : [] };
          } catch {
            return { ...t, ranking: [] };
          }
        })
      );
      setTournaments(tournamentsWithRanking);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/signin');
        return;
      }
      console.error('Error fetching tournaments:', err);
      message.error('Failed to load tournaments');
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchMyTournaments();
  }, [fetchMyTournaments]);

  return (
    <HomeContainer>
      <Content style={{ padding: '16px' }}>
            <PageHeader>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <PageTitle>
                    <TrophyOutlined style={{ marginRight: '8px', color: '#0ECB81' }} />
                    My Tournaments
                  </PageTitle>
                  <PageSubtitle>
                    Tournaments you have participated in
                  </PageSubtitle>
                </div>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchMyTournaments}
                  aria-label="Refresh"
                  // Icon only on mobile: the word costs ~60px next to a title that already
                  // has to wrap at 360px.
                  style={{ borderColor: '#0ECB81', color: '#0ECB81', flexShrink: 0, ...(isMobile ? { width: 40 } : null) }}
                >
                  {isMobile ? null : 'Refresh'}
                </Button>
              </div>
            </PageHeader>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '48px' }}>
                <Spin size="large" />
              </div>
            ) : tournaments.length === 0 ? (
              <StyledCard>
                <div style={{ textAlign: 'center', padding: '32px', color: '#848E9C' }}>
                  <TrophyOutlined style={{ fontSize: 48, marginBottom: 16, display: 'block' }} />
                  <div style={{ fontSize: 16, marginBottom: 8 }}>No tournaments yet</div>
                  <div style={{ fontSize: 14 }}>You have not participated in any tournaments.</div>
                </div>
              </StyledCard>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {tournaments.map((t) => (
                  <StyledCard
                    key={t.id}
                    title={
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <TrophyOutlined style={{ color: '#0ECB81' }} />
                        {t.name}
                        {t.isActive ? (
                          <Tag color="green">Active</Tag>
                        ) : (
                          <Tag color="default">Ended</Tag>
                        )}
                      </span>
                    }
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: '12px', marginBottom: 16 }}>
                      <div>
                        <div style={{ color: '#848E9C', fontSize: 12, marginBottom: 2 }}>
                          <CalendarOutlined style={{ marginRight: 4 }} />
                          Start
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{formatDate(t.tournamentStart)}</div>
                      </div>
                      <div>
                        <div style={{ color: '#848E9C', fontSize: 12, marginBottom: 2 }}>
                          <CalendarOutlined style={{ marginRight: 4 }} />
                          End
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{formatDate(t.tournamentEnd)}</div>
                      </div>
                      <div>
                        <div style={{ color: '#848E9C', fontSize: 12, marginBottom: 2 }}>
                          <TeamOutlined style={{ marginRight: 4 }} />
                          Participants
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{t.participantCount ?? 0}</div>
                      </div>
                    </div>

                    {t.ranking && t.ranking.length > 0 ? (
                      <div style={{ paddingTop: 12, borderTop: '1px solid #2B3139' }}>
                        <div style={{ color: '#848E9C', fontSize: 12, marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                          <span>Leaderboard</span>
                          <span>
                            {t.ranking[0]?.asOf && `Updated ${formatTime(t.ranking[0].asOf)}`}
                            {t.ranking[0]?.asOf && t.ranking[0]?.rankReference && ' · '}
                            {t.ranking[0]?.rankReference && `Arrows vs ${t.ranking[0].rankReference}`}
                          </span>
                        </div>
                        {/* Header row. Dropped on mobile, where each row labels its own
                            numbers — five column headings do not fit 360px either. */}
                        <div style={{ display: isMobile ? 'none' : 'grid', gridTemplateColumns: '32px 44px 1fr 120px 120px', gap: '0 8px', padding: '0 10px 4px', fontSize: 11, color: '#848E9C' }}>
                          <span>#</span>
                          <span></span>
                          <span>Name</span>
                          <span style={{ textAlign: 'right' }}>Realized</span>
                          <span style={{ textAlign: 'right' }}>Total PnL</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #2B3139', borderRadius: 6, overflow: 'hidden' }}>
                          {t.ranking.map((r, i) => {
                            const realized = r.unrealizedPnL != null ? r.pnl - r.unrealizedPnL : null;
                            const isMe = myPortfolioNames.has(r.name);
                            const podium = PODIUM[r.rank];
                            return (
                            <div
                              key={`${t.id}-rank-${r.rank}`}
                              style={{
                                // The five fixed tracks add up to ~348px with their gaps,
                                // which leaves the 1fr name column nothing on a 360px
                                // screen and pushes both PnL figures off the edge. On
                                // mobile the row becomes two lines instead.
                                display: isMobile ? 'flex' : 'grid',
                                ...(isMobile
                                  ? { flexDirection: 'column' as const, gap: 2 }
                                  : { gridTemplateColumns: '32px 44px 1fr 120px 120px', gap: '0 8px', alignItems: 'center' }),
                                padding: isMobile ? '8px 10px' : '9px 10px',
                                // One table, faint zebra: every other row barely lifted so the eye can
                                // follow a row across; top 3 read from their rank colour, your own row
                                // from its gold.
                                background: isMe
                                  ? 'rgba(255, 215, 0, 0.10)'
                                  : podium
                                    ? podium.tint
                                    : i % 2 ? 'rgba(255, 255, 255, 0.025)' : 'transparent',
                                borderTop: i ? '1px solid rgba(255, 255, 255, 0.04)' : 'none',
                                boxShadow: isMe ? 'inset 3px 0 0 #FFD700' : podium ? `inset 3px 0 0 ${podium.edge}` : undefined,
                                fontSize: 13,
                              }}
                            >
                              {isMobile ? (
                                <>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                    <span style={{
                                      fontWeight: 700,
                                      flexShrink: 0,
                                      color: r.rank === 1 ? '#FFD700' : r.rank === 2 ? '#C0C0C0' : r.rank === 3 ? '#CD7F32' : '#E0E0E0',
                                    }}>
                                      #{r.rank}
                                    </span>
                                    <RankMove entry={r} />
                                    <span style={{ color: isMe ? '#FFD700' : '#E0E0E0', fontWeight: isMe ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {r.name}{isMe && ' ★'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#848E9C', flexWrap: 'wrap' }}>
                                    <span>
                                      Realized{' '}
                                      <b style={{ color: realized != null ? (realized >= 0 ? '#0ECB81' : '#F6465D') : '#848E9C' }}>
                                        {realized != null ? `${realized >= 0 ? '+' : ''}${formatNumber(realized)}` : '—'}
                                      </b>
                                    </span>
                                    <span>
                                      Total{' '}
                                      <b style={{ color: r.pnl >= 0 ? '#0ECB81' : '#F6465D' }}>
                                        {r.pnl >= 0 ? '+' : ''}{formatNumber(r.pnl)}
                                      </b>
                                    </span>
                                  </div>
                                </>
                              ) : (
                              <>
                              <span style={{
                                fontWeight: 700,
                                color: r.rank === 1 ? '#FFD700' : r.rank === 2 ? '#C0C0C0' : r.rank === 3 ? '#CD7F32' : '#E0E0E0',
                              }}>
                                #{r.rank}
                              </span>
                              <RankMove entry={r} />
                              <span style={{ color: isMe ? '#FFD700' : '#E0E0E0', fontWeight: isMe ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {r.name}{isMe && ' ★'}
                              </span>
                              <span style={{ color: realized != null ? (realized >= 0 ? '#0ECB81' : '#F6465D') : '#848E9C', fontWeight: 600, textAlign: 'right' }}>
                                {realized != null ? `${realized >= 0 ? '+' : ''}${formatNumber(realized)}` : '—'}
                              </span>
                              <span style={{ color: r.pnl >= 0 ? '#0ECB81' : '#F6465D', fontWeight: 600, textAlign: 'right' }}>
                                {r.pnl >= 0 ? '+' : ''}{formatNumber(r.pnl)}
                              </span>
                              </>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#848E9C', fontSize: 13, paddingTop: 8, borderTop: '1px solid #2B3139' }}>
                        No ranking data available.
                      </div>
                    )}
                  </StyledCard>
                ))}
              </div>
            )}
      </Content>
    </HomeContainer>
  );
};

export default TournamentPage;
